import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, FolderKanban, MessageSquare, Search, CheckSquare, Users } from "lucide-react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const GROUPS = [
  { key: "projects", label: "Projekte", icon: FolderKanban, title: (r) => r.name, sub: (r) => r.client_name },
  { key: "clients", label: "Kunden", icon: Users, title: (r) => r.company, sub: (r) => r.contact_person },
  { key: "tasks", label: "Aufgaben", icon: CheckSquare, title: (r) => r.title, sub: (r) => r.project_name },
  { key: "comments", label: "Kommentare", icon: MessageSquare, title: (r) => r.message, sub: (r) => r.project_name },
  { key: "files", label: "Dateien", icon: FileText, title: (r) => r.name, sub: (r) => r.project_name },
];

export function SearchDialog({ open, onOpenChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    if (!open) {
      setQ("");
      setResults(null);
    }
  }, [open]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) {
      setResults(null);
      return;
    }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data);
      } catch {
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q]);

  const go = (link) => {
    onOpenChange(false);
    navigate(link);
  };

  const hasResults = results && GROUPS.some((g) => results[g.key]?.length);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[15%] max-w-xl translate-y-0 rounded-2xl p-0 overflow-hidden" data-testid="search-dialog">
        <DialogHeader className="sr-only">
          <DialogTitle>Globale Suche</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-3 border-b border-[#1D225C]/10 px-4">
          <Search className="h-4 w-4 text-[#B88A4A]" />
          <input
            data-testid="global-search-input"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Projekte, Kunden, Aufgaben, Dateien durchsuchen…"
            className="h-14 flex-1 bg-transparent text-sm outline-none placeholder:text-[#999]"
          />
        </div>
        <div className="max-h-[55vh] overflow-y-auto p-2">
          {loading && <p className="px-3 py-6 text-center text-sm text-[#777]">Suche läuft…</p>}
          {!loading && q.trim().length >= 2 && !hasResults && (
            <p className="px-3 py-6 text-center text-sm text-[#777]" data-testid="search-no-results">
              Keine Ergebnisse für „{q}".
            </p>
          )}
          {!loading && q.trim().length < 2 && (
            <p className="px-3 py-6 text-center text-sm text-[#777]">Mindestens 2 Zeichen eingeben.</p>
          )}
          {results &&
            GROUPS.map((g) =>
              results[g.key]?.length ? (
                <div key={g.key} className="mb-1">
                  <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#B88A4A]">
                    {g.label}
                  </p>
                  {results[g.key].map((r) => (
                    <button
                      key={g.key + r.id}
                      data-testid={`search-result-${g.key}-${r.id}`}
                      onClick={() => go(r.link)}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-[#B88A4A]/10"
                    >
                      <g.icon className="h-4 w-4 shrink-0 text-[#1D225C]/50" />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-[#1D225C]">{g.title(r)}</span>
                        {g.sub(r) && <span className="block truncate text-xs text-[#777]">{g.sub(r)}</span>}
                      </span>
                    </button>
                  ))}
                </div>
              ) : null
            )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
